import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer'
import type { Resume, ResumeSectionKey } from '../../../types'
import {
  joinDateRange,
  getAccent,
  getSectionOrder,
  labelFor,
  displayUrl,
  BULLET,
  pdfMetadata,
  pdfFontFamily,
  previewFontFamily,
  type PdfFontFamily,
} from './shared'

const TEXT = '#111827'
const MUTED = '#4b5563'
const BORDER = '#e5e7eb'
const SIDEBAR_BG = '#f8fafc'

const makeBaseStyles = (font: PdfFontFamily) =>
  StyleSheet.create({
    page: { fontSize: 10.5, color: TEXT, fontFamily: font.regular },
    row: { flexDirection: 'row', flex: 1 },
    sidebar: { width: 180, paddingTop: 36, paddingBottom: 50, paddingHorizontal: 18, backgroundColor: SIDEBAR_BG },
    main: { flex: 1, paddingTop: 36, paddingBottom: 50, paddingHorizontal: 28 },

    // Header (in main column)
    headerName: { fontSize: 22, fontFamily: font.bold, lineHeight: 1.1 },
    headerHeadline: { fontSize: 11, marginTop: 3 },
    divider: { borderBottomWidth: 1, borderBottomColor: BORDER, marginVertical: 10 },

    // Sidebar
    sidebarBlock: { marginBottom: 14 },
    sidebarHeading: { fontSize: 9.5, fontFamily: font.bold, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
    sidebarText: { fontSize: 9.5, color: TEXT, lineHeight: 1.4 },
    sidebarMeta: { fontSize: 9, color: MUTED, lineHeight: 1.4 },
    sidebarLink: { fontSize: 9.5, lineHeight: 1.4, textDecoration: 'none' },
    sidebarItem: { marginBottom: 6 },

    // Main column sections
    section: { marginBottom: 12 },
    sectionTitle: { fontSize: 11, fontFamily: font.bold, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 },
    itemHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
    itemTitle: { fontSize: 11, fontFamily: font.bold },
    itemMeta: { fontSize: 9.5, color: MUTED },
    bulletRow: { flexDirection: 'row', marginTop: 2 },
    bulletDot: { width: 8, fontSize: 10.5 },
    bulletText: { flex: 1, fontSize: 10.5, lineHeight: 1.4 },
    paragraph: { fontSize: 10.5, lineHeight: 1.45 },
    link: { textDecoration: 'none' },

    // Footer
    pageNumber: { position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center', fontSize: 8.5, color: MUTED },
  })

type ModernStyles = ReturnType<typeof makeBaseStyles>

export function ModernPdf({ resume }: { resume: Resume }) {
  const accent = getAccent(resume)
  const contact = resume.contact
  const meta = pdfMetadata(resume)
  const order = getSectionOrder(resume)
  const font = pdfFontFamily(resume, 'sans')
  const baseStyles = makeBaseStyles(font)

  return (
    <Document title={meta.title} author={meta.author} subject={meta.subject}>
      <Page size="A4" style={baseStyles.page}>
        <View style={baseStyles.row}>
          {/* Sidebar */}
          <View style={baseStyles.sidebar}>
            {/* Contact block */}
            {(contact.email || contact.phone || contact.location || contact.website || contact.linkedin || contact.github) && (
              <View style={baseStyles.sidebarBlock}>
                <Text style={[baseStyles.sidebarHeading, { color: accent }]}>Contact</Text>
                {contact.email && (
                  <Link src={`mailto:${contact.email}`} style={[baseStyles.sidebarLink, { color: TEXT }]}>
                    {contact.email}
                  </Link>
                )}
                {contact.phone && <Text style={baseStyles.sidebarText}>{contact.phone}</Text>}
                {contact.location && <Text style={baseStyles.sidebarText}>{contact.location}</Text>}
                {contact.website && (
                  <Link src={contact.website} style={[baseStyles.sidebarLink, { color: accent }]}>
                    {displayUrl(contact.website)}
                  </Link>
                )}
                {contact.linkedin && (
                  <Link src={contact.linkedin} style={[baseStyles.sidebarLink, { color: accent }]}>
                    {displayUrl(contact.linkedin)}
                  </Link>
                )}
                {contact.github && (
                  <Link src={contact.github} style={[baseStyles.sidebarLink, { color: accent }]}>
                    {displayUrl(contact.github)}
                  </Link>
                )}
              </View>
            )}

            {/* Skills in sidebar */}
            {resume.skills.length > 0 && (
              <View style={baseStyles.sidebarBlock} wrap={false}>
                <Text style={[baseStyles.sidebarHeading, { color: accent }]}>{labelFor(resume, 'skills')}</Text>
                {resume.skills.map((g) => (
                  <View key={g.id} style={baseStyles.sidebarItem}>
                    <Text style={{ fontSize: 9.5, fontWeight: 700 }}>{g.category}</Text>
                    <Text style={baseStyles.sidebarMeta}>{g.items.join(', ')}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Languages in sidebar */}
            {resume.languages.length > 0 && (
              <View style={baseStyles.sidebarBlock} wrap={false}>
                <Text style={[baseStyles.sidebarHeading, { color: accent }]}>{labelFor(resume, 'languages')}</Text>
                {resume.languages.map((l) => (
                  <Text key={l.id} style={baseStyles.sidebarText}>
                    {l.name}{l.proficiency ? ` (${l.proficiency})` : ''}
                  </Text>
                ))}
              </View>
            )}

            {/* Certifications in sidebar */}
            {resume.certifications.length > 0 && (
              <View style={baseStyles.sidebarBlock} wrap={false}>
                <Text style={[baseStyles.sidebarHeading, { color: accent }]}>{labelFor(resume, 'certifications')}</Text>
                {resume.certifications.map((c) => (
                  <View key={c.id} style={baseStyles.sidebarItem}>
                    <Text style={{ fontSize: 9.5, fontWeight: 700 }}>{c.name}</Text>
                    {(c.issuer || c.date) && (
                      <Text style={baseStyles.sidebarMeta}>
                        {[c.issuer, c.date].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Main column */}
          <View style={baseStyles.main}>
            <View>
              <Text style={baseStyles.headerName}>{contact.fullName || ' '}</Text>
              {contact.headline ? <Text style={[baseStyles.headerHeadline, { color: accent }]}>{contact.headline}</Text> : null}
            </View>
            <View style={baseStyles.divider} />

            {order.map((key) => renderMainSection(key, resume, accent, baseStyles))}
          </View>
        </View>

        {/* Page footer */}
        <Text
          style={baseStyles.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) => (totalPages > 1 ? `${pageNumber} / ${totalPages}` : '')}
        />
      </Page>
    </Document>
  )
}

// Sidebar already renders skills/languages/certifications — main column
// renders the rest in the user's chosen order.
function renderMainSection(key: ResumeSectionKey, resume: Resume, accent: string, baseStyles: ModernStyles) {
  switch (key) {
    case 'summary':
      if (!resume.summary) return null
      return (
        <View key={key} style={baseStyles.section} wrap={false}>
          <Text style={[baseStyles.sectionTitle, { color: accent }]}>{labelFor(resume, 'summary')}</Text>
          <Text style={baseStyles.paragraph}>{resume.summary}</Text>
        </View>
      )
    case 'experience':
      if (resume.experience.length === 0) return null
      return (
        <View key={key} style={baseStyles.section}>
          <Text style={[baseStyles.sectionTitle, { color: accent }]}>{labelFor(resume, 'experience')}</Text>
          {resume.experience.map((exp) => (
            <View key={exp.id} style={{ marginBottom: 8 }} wrap={false}>
              <View style={baseStyles.itemHeader}>
                <Text style={baseStyles.itemTitle}>
                  {exp.role}{exp.company ? ` · ${exp.company}` : ''}
                </Text>
                <Text style={baseStyles.itemMeta}>{joinDateRange(exp.startDate, exp.endDate)}</Text>
              </View>
              {exp.location ? <Text style={baseStyles.itemMeta}>{exp.location}</Text> : null}
              {exp.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={baseStyles.bulletRow}>
                  <Text style={baseStyles.bulletDot}>{BULLET}</Text>
                  <Text style={baseStyles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'projects':
      if (resume.projects.length === 0) return null
      return (
        <View key={key} style={baseStyles.section}>
          <Text style={[baseStyles.sectionTitle, { color: accent }]}>{labelFor(resume, 'projects')}</Text>
          {resume.projects.map((p) => (
            <View key={p.id} style={{ marginBottom: 6 }} wrap={false}>
              <View style={baseStyles.itemHeader}>
                <Text style={baseStyles.itemTitle}>{p.name}</Text>
                {p.url ? (
                  <Link src={p.url} style={[baseStyles.link, { color: accent, fontSize: 9.5 }]}>
                    {displayUrl(p.url)}
                  </Link>
                ) : null}
              </View>
              {p.description ? <Text style={baseStyles.paragraph}>{p.description}</Text> : null}
              {p.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={baseStyles.bulletRow}>
                  <Text style={baseStyles.bulletDot}>{BULLET}</Text>
                  <Text style={baseStyles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'education':
      if (resume.education.length === 0) return null
      return (
        <View key={key} style={baseStyles.section}>
          <Text style={[baseStyles.sectionTitle, { color: accent }]}>{labelFor(resume, 'education')}</Text>
          {resume.education.map((e) => (
            <View key={e.id} style={{ marginBottom: 6 }} wrap={false}>
              <View style={baseStyles.itemHeader}>
                <Text style={baseStyles.itemTitle}>
                  {[e.degree, e.field].filter(Boolean).join(', ') || e.institution}
                </Text>
                <Text style={baseStyles.itemMeta}>{joinDateRange(e.startDate, e.endDate)}</Text>
              </View>
              <Text style={baseStyles.itemMeta}>{[e.institution, e.location].filter(Boolean).join(' · ')}</Text>
              {e.details ? <Text style={baseStyles.paragraph}>{e.details}</Text> : null}
            </View>
          ))}
        </View>
      )
    case 'customSections':
      return resume.customSections.map((s) => (
        <View key={s.id} style={baseStyles.section} wrap={false}>
          <Text style={[baseStyles.sectionTitle, { color: accent }]}>{s.title}</Text>
          <Text style={baseStyles.paragraph}>{s.body}</Text>
        </View>
      ))
    // Skills, certifications, languages render in the sidebar already.
    case 'skills':
    case 'certifications':
    case 'languages':
      return null
  }
}

/**
 * HTML preview — mirrors the PDF layout for the editor's live pane.
 */
export function ModernPreview({ resume }: { resume: Resume }) {
  const accent = getAccent(resume)
  const contact = resume.contact
  const order = getSectionOrder(resume)
  return (
    <div className="bg-white text-slate-900 shadow-sm rounded-md overflow-hidden text-[12px] leading-snug flex" style={{ fontFamily: previewFontFamily(resume, 'sans') }}>
      {/* Sidebar */}
      <aside className="w-[180px] bg-slate-50 p-5 space-y-4 shrink-0">
        {(contact.email || contact.phone || contact.location || contact.website || contact.linkedin || contact.github) && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>Contact</div>
            <div className="space-y-0.5 text-[10.5px] break-all">
              {contact.email && <div>{contact.email}</div>}
              {contact.phone && <div>{contact.phone}</div>}
              {contact.location && <div>{contact.location}</div>}
              {contact.website && <div style={{ color: accent }}>{displayUrl(contact.website)}</div>}
              {contact.linkedin && <div style={{ color: accent }}>{displayUrl(contact.linkedin)}</div>}
              {contact.github && <div style={{ color: accent }}>{displayUrl(contact.github)}</div>}
            </div>
          </div>
        )}
        {resume.skills.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>{labelFor(resume, 'skills')}</div>
            <div className="space-y-1.5 text-[10.5px]">
              {resume.skills.map((g) => (
                <div key={g.id}>
                  <div className="font-semibold">{g.category}</div>
                  <div className="text-slate-600">{g.items.join(', ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {resume.languages.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>{labelFor(resume, 'languages')}</div>
            <div className="space-y-0.5 text-[10.5px]">
              {resume.languages.map((l) => (
                <div key={l.id}>{l.name}{l.proficiency ? ` (${l.proficiency})` : ''}</div>
              ))}
            </div>
          </div>
        )}
        {resume.certifications.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>{labelFor(resume, 'certifications')}</div>
            <div className="space-y-1 text-[10.5px]">
              {resume.certifications.map((c) => (
                <div key={c.id}>
                  <div className="font-semibold">{c.name}</div>
                  {(c.issuer || c.date) && (
                    <div className="text-slate-600 text-[10px]">{[c.issuer, c.date].filter(Boolean).join(' · ')}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 p-8">
        <div>
          <div className="text-2xl font-bold">{contact.fullName || '\u00a0'}</div>
          {contact.headline && <div style={{ color: accent }} className="mt-0.5">{contact.headline}</div>}
        </div>
        <hr className="my-3 border-slate-200" />

        {order.map((key) => {
          switch (key) {
            case 'summary':
              if (!resume.summary) return null
              return (
                <Section key={key} title={labelFor(resume, 'summary')} accent={accent}>
                  <p>{resume.summary}</p>
                </Section>
              )
            case 'experience':
              if (resume.experience.length === 0) return null
              return (
                <Section key={key} title={labelFor(resume, 'experience')} accent={accent}>
                  {resume.experience.map((e) => (
                    <div key={e.id} className="mb-3">
                      <div className="flex justify-between">
                        <div className="font-semibold">{e.role}{e.company ? ` · ${e.company}` : ''}</div>
                        <div className="text-slate-500 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div>
                      </div>
                      {e.location && <div className="text-slate-500 text-[11px]">{e.location}</div>}
                      <ul className="list-disc pl-5 mt-1">
                        {e.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    </div>
                  ))}
                </Section>
              )
            case 'projects':
              if (resume.projects.length === 0) return null
              return (
                <Section key={key} title={labelFor(resume, 'projects')} accent={accent}>
                  {resume.projects.map((p) => (
                    <div key={p.id} className="mb-2">
                      <div className="flex justify-between">
                        <div className="font-semibold">{p.name}</div>
                        {p.url && <a className="text-[11px]" style={{ color: accent }} href={p.url}>{displayUrl(p.url)}</a>}
                      </div>
                      {p.description && <p>{p.description}</p>}
                      <ul className="list-disc pl-5">
                        {p.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    </div>
                  ))}
                </Section>
              )
            case 'education':
              if (resume.education.length === 0) return null
              return (
                <Section key={key} title={labelFor(resume, 'education')} accent={accent}>
                  {resume.education.map((e) => (
                    <div key={e.id} className="mb-2">
                      <div className="flex justify-between">
                        <div className="font-semibold">{[e.degree, e.field].filter(Boolean).join(', ') || e.institution}</div>
                        <div className="text-slate-500 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div>
                      </div>
                      <div className="text-slate-500 text-[11px]">{[e.institution, e.location].filter(Boolean).join(' · ')}</div>
                      {e.details && <p>{e.details}</p>}
                    </div>
                  ))}
                </Section>
              )
            case 'customSections':
              return resume.customSections.map((s) => (
                <Section key={s.id} title={s.title} accent={accent}>
                  <p className="whitespace-pre-line">{s.body}</p>
                </Section>
              ))
            case 'skills':
            case 'certifications':
            case 'languages':
              return null
          }
        })}

        {/* Empty-state hint shown only when the doc is essentially blank */}
        {!resume.summary && resume.experience.length === 0 && resume.projects.length === 0 && resume.education.length === 0 && (
          <p className="text-slate-400 italic text-[11px] mt-4">Start adding sections on the left to fill out your resume.</p>
        )}
      </div>
    </div>
  )
}

function Section({ title, accent, children }: { title: string; accent: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>{title}</div>
      <div>{children}</div>
    </div>
  )
}

